import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";

import { Pager } from "rhombus-react";

import { CreativeVersions } from "reducers/CreativeVersions";
import { loadLibrary } from "reducers/LibraryProperties";
import { clearDetails, loadCreative } from "reducers/CreativeDetails";
import { AdvertiserDetails } from "reducers/AdvertiserDetails";
import { Labels } from "reducers/Labels";

import { CREATIVE_ROW_COUNT_OPTIONS } from "utils/constants";
import { CREATIVE_VERSION_COLUMNS } from "widgets/grid/columns/Versions";

import InfoHeader from "widgets/toolbars/InfoHeader";

import Modals from "./versions/Modals";
import Toolbar from "./versions/Toolbar";
import FilterTagList from "widgets/toolbars/FilterTagList";
import Grid from "./versions/Grid";

import { useCollectionFiltering } from "utils/DataGrid";
import {
	VERSION_PREVIEW_KEY,
	enablePreviewVersion2
} from "utils/Flags";
import { setKey, removeKey } from "utils/Keyboard";
import useCancelToken from "hooks/useCancelToken";
import { isLibraryLoadedForId } from "selectors/LibraryProperties";

import "./CreativeVersions.css";

const distinctColumns = CREATIVE_VERSION_COLUMNS
	// filter out labelsCnt since we're hardcoding the value as it isn't supported in the distincts endpoint
	.filter(v => v.key !== "labelsCnt" && /checkbox|range/gi.test(v.filter))
	.map(v => v.key);

export default function VersionList({ libraryID, creativeId }){
	const dispatch = useDispatch();

	const creativeVersions = useSelector(state => state.creativeVersions);
	const libraryProperties = useSelector(state => state.libraryProperties);
	const advertiserId = libraryProperties?.advertiser?.id;
	const isLibraryLoaded = useSelector(state => isLibraryLoadedForId(state, libraryID));

	const { getNewToken } = useCancelToken(true);
	const { getNewToken: getNewLibraryToken, cancel: cancelLibraryToken } = useCancelToken(true);

	const {
		filterInfo: { page, rpp, searchTerm, sort },
		columnFilters,
	} = useCollectionFiltering(
		creativeVersions,
		CreativeVersions.setAdvancedFilter,
		() => {
			dispatch(CreativeVersions.clearCreativeVersions()).then(() => {
				return dispatch(CreativeVersions.loadCreativeVersions(libraryID, creativeId, getNewToken()));
			});
		},
		() => {
			dispatch(CreativeVersions.clearColumnFilters());
			dispatch(CreativeVersions.resetAllFilters()); // doesn't actually reset sort order
		}
	);

	useEffect(() => {
		dispatch(loadLibrary(libraryID, getNewLibraryToken()));
		return cancelLibraryToken;
	}, [libraryID]);

	useEffect(() => {
		if (isLibraryLoaded && advertiserId) {
			dispatch(AdvertiserDetails.loadAdvertiserDetails(libraryID));
		}
	}, [isLibraryLoaded, advertiserId, libraryID]);

	useEffect(() => {
		dispatch(clearDetails());
		dispatch(loadCreative(creativeId));
		dispatch(CreativeVersions.loadCreativeVersionsDistincts(libraryID, creativeId, distinctColumns));

		/*
			As of now the distincts endpoint does not support labelsCnt so filtering doesn't work properly.
			For now we're hardcoding the value of labelsCnt and updating it here directly
		*/
		dispatch(CreativeVersions.updateColumnFilter("labelsCnt", {
			options: [0, 50], // eslint-disable-line no-magic-numbers
		}));
	}, [libraryID, creativeId]);

	useEffect(() => {
		// advertiser id is initially not in the libraryProperties obj on load
		if(advertiserId) {
			dispatch(Labels.loadAdvertiserTaxonomy(advertiserId));
		}
	}, [advertiserId]);

	const pagerProps = {
		showItemCount: true,
		initialPage: page,
		forcePage: page,
		pageSize: rpp,
		pageCount: creativeVersions.totalPages,
		maxItems: creativeVersions.totalItems,
		showItemWord: "",
		rowCountItems: CREATIVE_ROW_COUNT_OPTIONS,

		disabled: !creativeVersions.isLoaded,

		onPageChange: newPage => dispatch(CreativeVersions.setPage(newPage)),
		onRowCountChange: newRPP => dispatch(CreativeVersions.setRPP(newRPP)),
	};

	/**
	 * Set Up Keyboard Shortcut Feature Flags
	 * See utils/Flags for more info
	 */
	useEffect(() => {
		setKey(VERSION_PREVIEW_KEY, enablePreviewVersion2);

		return () => {
			removeKey(VERSION_PREVIEW_KEY);
			dispatch(clearDetails());
			dispatch(AdvertiserDetails.resetDetails());
			dispatch(CreativeVersions.clearCreativeVersions());
			dispatch(Labels.clearLabels());
		};
	}, []);

	return (
		<div className="creative-versions-list">
			<Modals
				libraryId = {libraryID}
				creativeId = {creativeId}
				returnPath = {`/library/${libraryID}/creative/${creativeId}/versions/`}
			/>
			<InfoHeader type="creative" />
			<Toolbar
				searchTerm = {searchTerm}
				handleSearch = {newSearchTerm => dispatch(CreativeVersions.setSearchTerm(newSearchTerm))}
				creativeId = {creativeId}
				libraryId = {libraryID}
				isLoading = {!creativeVersions.isLoaded}
			/>
			<FilterTagList
				Reducer = {CreativeVersions}
				filters = {columnFilters}
			/>
			<Grid
				libraryId = {libraryID}
				creativeId = {creativeId}
				isLoading={!creativeVersions.isLoaded}
				sort={sort} // Needed to display the initial column sort order indicator
			/>
			<Pager
				{...pagerProps}
			/>
		</div>
	);
}

VersionList.displayName = "VersionList";
